import { Notification } from './NotificationService';
import { getApiBaseUrl } from '../utils/server-config';

export interface NotificationListResponse {
    data: Notification[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
}

export class NotificationApi {
    private baseUrl: string;

    constructor(baseUrl: string, private getToken: () => string | null) {
        this.baseUrl = baseUrl;
    }

    // Factory method to create API instance with environment configuration
    static create(getToken: () => string | null): NotificationApi {
        return new NotificationApi(getApiBaseUrl(), getToken);
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const token = this.getToken();

        if (!token) {
            throw new Error('No authentication token available');
        }

        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                ...options,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error(`[NotificationApi] Request failed for ${endpoint}:`, error);
            throw error;
        }
    }

    async getNotifications(page = 1, limit = 20, unreadOnly = false): Promise<NotificationListResponse> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            ...(unreadOnly && { unreadOnly: 'true' }),
        });

        const response = await this.request<any>(`/notifications/user?${params}`);

        // Map backend notifications to your UI format
        const mappedNotifications = response.data?.map((notification: any) => this.mapBackendNotification(notification)) || [];

        return {
            data: mappedNotifications,
            pagination: response.pagination || {
                page,
                limit,
                total: mappedNotifications.length,
                totalPages: 1
            }
        };
    }

    async getUnreadCount(): Promise<number> {
        try {
            const response = await this.request<{ count: number }>('/notifications/user/unread-count');
            return response.count || 0;
        } catch (error) {
            console.error('[NotificationApi] Failed to get unread count:', error);
            return 0;
        }
    }

    async markAsRead(notificationId: string): Promise<void> {
        await this.request(`/notifications/${notificationId}/read`, {
            method: 'PATCH',
        });
    }

    async markAllAsRead(): Promise<void> {
        await this.request('/notifications/user/mark-all-read', {
            method: 'PATCH',
        });
    }

    // Map backend notification format to your UI format (same as in NotificationService)
    private mapBackendNotification(backendNotification: any): Notification {
        const mapNotificationType = (backendType?: string): Notification['type'] => {
            if (!backendType) return 'general';

            const typeMap: Record<string, Notification['type']> = {
                'project_drawing': 'project_drawing',
                'comment': 'comment',
                'drawing_sent': 'drawing_sent',
                'upload_failed': 'upload_failed',
                'project_update': 'general',
                'system': 'general',
                'chat': 'general',
                'message': 'general',
                'new_message': 'general',
                'chat_message': 'general'
            };

            return typeMap[backendType.toLowerCase()] || 'general';
        };

        const extractClientInfo = (data?: Record<string, any>) => {
            return {
                clientName: data?.clientName || data?.client?.name || undefined,
                estimateId: data?.estimateId || data?.projectId || undefined
            };
        };

        const { clientName, estimateId } = extractClientInfo(backendNotification.data);

        return {
            id: backendNotification.id,
            type: mapNotificationType(backendNotification.type),
            title: backendNotification.title,
            description: backendNotification.body || backendNotification.description || '',
            timestamp: backendNotification.createdAt
                ? new Date(backendNotification.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                })
                : new Date().toLocaleString(),
            isRead: backendNotification.read || false,
            clientName,
            estimateId,
            data: backendNotification.data
        };
    }
}